import {
  Lock,
  MoreHorizontal,
  Pencil,
  Trash2,
  Upload,
  History,
} from "lucide-react";
import { SideSheet, Dropdown } from "@/components/ui";
import { useTranslation } from "@audit-app/utils";
import { FileIcon } from "./FileIcon";
import { getCategoryStyle, statusLabel, statusToBadge } from "./constants";
import type { DocumentView } from "./types";

interface DocumentDetailHeaderProps {
  document: DocumentView;
  onUploadNewVersion: () => void;
  onRename: () => void;
  onDelete: () => void;
  onDownloadPreviousVersion: () => void;
  hasPreviousVersion: boolean;
}

export function DocumentDetailHeader({
  document,
  onUploadNewVersion,
  onRename,
  onDelete,
  onDownloadPreviousVersion,
  hasPreviousVersion,
}: DocumentDetailHeaderProps) {
  const { t } = useTranslation(["documents"]);
  const badgeStatus = statusToBadge[document.status];
  const catStyle = getCategoryStyle();
  const categoryName =
    document.categoryName ?? t("documents:documentDetailHeader.uncategorized");
  const showVersionBadge =
    document.currentVersion !== undefined && document.currentVersion > 1;

  // Build tags for header
  const headerTags: Array<{
    label: string;
    color?: string;
    bgColor?: string;
    variant?: "default" | "status";
  }> = [
    {
      label: statusLabel[document.status],
      variant: "status" as const,
      ...(() => {
        const colors: Record<string, { color: string; bgColor: string }> = {
          completed: {
            color: "#34d399",
            bgColor: "rgba(52, 211, 153, 0.15)",
          },
          active: { color: "#fbbf24", bgColor: "rgba(251, 191, 36, 0.15)" },
          draft: { color: "#a1a1aa", bgColor: "rgba(161, 161, 170, 0.15)" },
          overdue: { color: "#f87171", bgColor: "rgba(248, 113, 113, 0.15)" },
        };
        return colors[badgeStatus] || {};
      })(),
    },
    {
      label: categoryName,
      color: catStyle.text,
      bgColor: catStyle.bg,
    },
  ];

  // Show version badge in header when version > 1
  if (showVersionBadge) {
    headerTags.push({
      label: `v${document.currentVersion}`,
      color: "#38bdf8",
      bgColor: "rgba(56, 189, 248, 0.15)",
    });
  }

  // Build icon with optional lock badge
  const headerIcon = (
    <div className="relative">
      <FileIcon type={document.type} />
      {document.confidential && (
        <div className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-surface flex items-center justify-center">
          <Lock size={9} className="text-amber-400" />
        </div>
      )}
    </div>
  );

  // Build header actions
  const headerActions = (
    <Dropdown>
      <Dropdown.Trigger>
        <button
          type="button"
          className="h-8 w-8 rounded-lg flex items-center justify-center text-muted hover:text-primary hover:bg-card-hover transition-colors cursor-pointer"
          aria-label="More actions"
        >
          <MoreHorizontal size={16} />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content side="bottom" align="end">
        <Dropdown.Item
          icon={<Upload size={14} />}
          onSelect={onUploadNewVersion}
        >
          {t("documents:documentDetailHeader.uploadNewVersion")}
        </Dropdown.Item>
        <Dropdown.Item icon={<Pencil size={14} />} onSelect={onRename}>
          {t("documents:documentDetailHeader.rename")}
        </Dropdown.Item>
        {hasPreviousVersion && (
          <Dropdown.Item
            icon={<History size={14} />}
            onSelect={onDownloadPreviousVersion}
          >
            {t("documents:documentDetailHeader.downloadPreviousVersion")}
          </Dropdown.Item>
        )}
        <Dropdown.Separator />
        <Dropdown.Item
          icon={<Trash2 size={14} />}
          destructive
          onSelect={onDelete}
        >
          {t("documents:documentDetailHeader.delete")}
        </Dropdown.Item>
      </Dropdown.Content>
    </Dropdown>
  );

  return (
    <SideSheet.Header
      title={document.title}
      icon={headerIcon}
      tags={headerTags}
      actions={headerActions}
    />
  );
}
